import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button, TooltipProvider } from '@staff-app/ui';
import type { MdmCommandDefinition } from '@staff-app/shared-types';
import type { MdmDevice } from '@/features/mdm-devices/hooks/useStaffCustomerDevices';
import { MdmActionButton } from '@/features/customers/components/actions/MdmActionButton';
import { MdmActionDropdown } from '@/features/customers/components/actions/MdmActionDropdown';
import { MdmConfirmDialog } from '@/features/customers/components/actions/MdmConfirmDialog';
import {
  MDM_ACTION_HANDLERS,
  useMdmDispatchContext,
} from '@/features/customers/components/actions/MdmActionHandlers';
import { useMdmActionState } from '@/features/customers/components/actions/useMdmActionState';
import { computeVisibleCount } from './lib/action-overflow';
import { resolveTestingCommands, TESTING_ACTION_LAYOUT } from './lib/testing-actions';
import { useDrawerScopeVars } from '../../lib/drawer-mode';

/** Matches the row's `gap-1.5` (6px). */
const GAP_PX = 6;

interface Props {
  /** The drawer's merged device (list row + live detail) — must carry `feature` for the toggle. */
  device: MdmDevice & { feature?: Record<string, unknown> | null };
  /** open-api `availableCommands` for this device (already plan ∩ classification ∩ state). */
  availableCommands: readonly string[] | null | undefined;
  teamId?: string | null;
  /** After any command succeeds: refetch detail, activity, staff actions, command history. */
  onSettled: () => void;
  openChildDialog: (dialogId: string, context?: Record<string, unknown>) => void;
  /** Take screenshot runs the drawer's capture flow (capture + wait + show on the Content tab). */
  onCaptureScreenshot: () => Promise<void> | void;
  capturing?: boolean;
  /** Non-null → the screenshot button is disabled with this as its tooltip. */
  screenshotBlockedReason?: string | null;
}

/**
 * The device drawer action bar — the vendored MDM action row (catalog confirm levels, dispatch
 * registry) restricted to `TESTING_ACTION_ALLOWLIST ∩ availableCommands`. Mounted in both drawer
 * modes (My Testing Devices and the customer drawer, where `availableCommands` already reflects the
 * customer's plan); renders nothing when no command survives the intersection.
 *
 * Presentation is scio3's drawer action bar: a white row pinned under the banner, soft teal buttons
 * (Reboot as the red-outline anchor), and a neutral "More". Like scio3, the toolbar shows as many of
 * its (priority-ordered) buttons as FIT on one line — a hidden measuring row gives natural widths —
 * and the rest join the overflow commands in More.
 */
export function TestingActionBar({
  device,
  availableCommands,
  teamId,
  onSettled,
  openChildDialog,
  onCaptureScreenshot,
  capturing = false,
  screenshotBlockedReason,
}: Props) {
  const { toolbar, overflow } = useMemo(
    () => resolveTestingCommands(availableCommands, device.feature),
    [availableCommands, device.feature],
  );
  const { loading, setLoading, pending, requestConfirm, clearConfirm } = useMdmActionState();
  // Customer drawer: identify carries the page's account; testing mode has none (unchanged).
  const { accountId } = useDrawerScopeVars();
  const ctx = useMdmDispatchContext({
    device,
    teamId,
    refetchDevice: onSettled,
    openChildDialog,
    accountId,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  // Assume everything fits; the layout effect corrects it before paint (and on resize). No-layout
  // environments (jsdom) keep "show all".
  const [visibleCount, setVisibleCount] = useState(toolbar.length);
  const toolbarKey = toolbar.map((c) => `${c.id}:${c.i18nKey}`).join(',');
  const hasOverflow = overflow.length > 0;

  useLayoutEffect(() => {
    const container = containerRef.current;
    const ghost = ghostRef.current;
    if (!container || !ghost) return;
    const measure = () => {
      const kids = Array.from(ghost.children) as HTMLElement[];
      if (kids.length < toolbar.length + 1) return;
      const widths = kids.slice(0, toolbar.length).map((el) => el.offsetWidth);
      const moreWidth = kids[toolbar.length].offsetWidth + GAP_PX;
      const available = container.clientWidth;
      let count: number;
      if (!hasOverflow || available <= 0) {
        count = computeVisibleCount({
          widths,
          moreWidth,
          available,
          gap: GAP_PX,
        });
      } else {
        // Overflow commands always need More, so its width is reserved up front.
        let used = moreWidth;
        count = 0;
        for (const w of widths) {
          if (used + w + GAP_PX > available) break;
          used += w + GAP_PX;
          count += 1;
        }
      }
      setVisibleCount(count);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    ro.observe(ghost);
    return () => ro.disconnect();
  }, [toolbarKey, toolbar.length, hasOverflow]);

  if (toolbar.length === 0 && overflow.length === 0) return null;

  const visible = toolbar.slice(0, visibleCount);
  const moreItems = [...toolbar.slice(visibleCount), ...overflow];

  const runNow = async (cmd: MdmCommandDefinition) => {
    if (cmd.id === 'MDM_CAPTURE_SCREENSHOT') {
      await onCaptureScreenshot();
      return;
    }
    setLoading(cmd.id);
    try {
      await MDM_ACTION_HANDLERS[cmd.action.type](cmd, ctx);
    } finally {
      setLoading(null);
    }
  };

  const run = (cmd: MdmCommandDefinition) => {
    if (cmd.confirmLevel && cmd.confirmLevel !== 'none') {
      requestConfirm(cmd);
      return;
    }
    void runNow(cmd);
  };

  const handleConfirm = () => {
    const cmd = pending;
    clearConfirm();
    if (cmd) void runNow(cmd);
  };

  const busy = loading ?? (capturing ? 'MDM_CAPTURE_SCREENSHOT' : null);

  return (
    <>
      <TooltipProvider delayDuration={0}>
        <div
          className="relative shrink-0 border-b bg-background px-6 py-2"
          data-testid="testing-action-bar"
        >
          <div ref={containerRef} className="flex flex-nowrap items-center gap-1.5 overflow-hidden">
            {visible.map((cmd) => (
              <MdmActionButton
                key={cmd.id}
                cmd={cmd}
                isLoading={busy === cmd.id}
                onClick={() => run(cmd)}
                layout={TESTING_ACTION_LAYOUT}
                disabledReason={
                  cmd.id === 'MDM_CAPTURE_SCREENSHOT' ? screenshotBlockedReason : null
                }
              />
            ))}
            <MdmActionDropdown
              items={moreItems}
              loading={busy}
              onSelect={run}
              layout={TESTING_ACTION_LAYOUT}
              disabledReasons={{
                MDM_CAPTURE_SCREENSHOT: screenshotBlockedReason,
              }}
            />
          </div>

          {/* Hidden measuring row — every toolbar command + a "More" trigger at natural width. Inert:
              no testids, not focusable, hidden from assistive tech. */}
          <div
            ref={ghostRef}
            aria-hidden
            className="pointer-events-none invisible absolute left-0 top-0 -z-10 flex flex-nowrap items-center gap-1.5"
          >
            {toolbar.map((cmd) => (
              <GhostButton key={cmd.id} cmd={cmd} />
            ))}
            <Button variant="outline" size="sm" className="shrink-0 gap-1.5" tabIndex={-1}>
              <span>{'More'}</span>
              <ChevronDown className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
      </TooltipProvider>

      <MdmConfirmDialog cmd={pending} onConfirm={handleConfirm} onClose={clearConfirm} />
    </>
  );
}

/** Same box as `MdmActionButton` (size sm, icon + label) for width measurement only. */
function GhostButton({ cmd }: { cmd: MdmCommandDefinition }) {
  return (
    <Button variant="secondary" size="sm" className="shrink-0 gap-1.5" tabIndex={-1}>
      <span className="h-4 w-4" />
      <span>{cmd.i18nKey}</span>
    </Button>
  );
}
